import Lottie from "lottie-react";
import loadingAnimationData from "../../../assets/animations/fileLoadingAnimation.json";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/auth-context";
import {
	IApplicationNumber,
	IApplicationNumberRemove,
	IApplicationNumberUpdate,
	IApplicationNumberUpdateNumbers,
	IBusinessNumberModel,
	IManagedApplicationSettings,
	TBrandingOptions,
} from "src/types/models/apiModels";
import { ApplicationSubmitProcessorViewModel } from "./application-submit-processor-viewmodel";
import ButtonPro from "src/components/atoms/primary-button/primary-button";

interface IApplicationSubmitProcessorProps {
	applicationId: string;
	applicationName: string;
	description: string;
	businessId: string;
	brandingOption: TBrandingOptions;
	numbersToProcess: IBusinessNumberModel[];
	numbersToUnlink: IBusinessNumberModel[];
	managedApplicationSettings?: IManagedApplicationSettings;
	onClose: () => void;
}

const ApplicationSubmitProcessor: React.FC<
	IApplicationSubmitProcessorProps
> = ({
	applicationId,
	applicationName,
	description,
	businessId,
	brandingOption,
	numbersToProcess,
	numbersToUnlink,
	managedApplicationSettings,
	onClose,
}) => {
	const { user } = useAuth();
	const viewModel = useRef<ApplicationSubmitProcessorViewModel | null>(null);
	const [isProcessing, setIsProcessing] = useState(true);
	const [errors, setErrors] = useState<string[]>([]);
	const [noOfAddedNumber, setNoOfAddedNumber] = useState(0);
	const [noOfUpdatedNumber, setNoOfUpdatedNumber] = useState(0);
	const [noOfUnLinkedNumber, setNoOfLinkedNumber] = useState(0);

	const delay = (ms: number) =>
		new Promise((resolve) => setTimeout(resolve, ms));

	useEffect(() => {
		const processNumbers = async () => {
			if (!user || !user.accessToken) return;

			viewModel.current = new ApplicationSubmitProcessorViewModel(
				user.accessToken
			);

			const numbersToAdd = numbersToProcess.filter(
				(p) => !p.applicationBundleCode
			);
			const numbersToUpdate = numbersToProcess.filter(
				(p) => p.applicationBundleCode && p.hasUnsavedChanges
			);

			setNoOfAddedNumber(numbersToAdd.length);
			setNoOfUpdatedNumber(numbersToUpdate.length);
			setNoOfLinkedNumber(numbersToUnlink.length);

			const businessNumbers: IApplicationNumberUpdateNumbers[] = [];

			numbersToAdd.map((number) => {
				const num: IApplicationNumber = {
					businessNumber: number.businessNumber,
					branding: number.branding,
					managedNumberSettings: number.managedNumberSettings,
				};

				const businessNumberToAdd: IApplicationNumberUpdateNumbers = {
					number: num,
					action: "Add",
				};

				businessNumbers.push(businessNumberToAdd);
			});

			numbersToUpdate.map((number) => {
				const num: IApplicationNumberUpdate = {
					branding: number.branding,
					businessNumber: number.businessNumber,
					managedNumberSettings: number.managedNumberSettings,
				};
				const businessNumberToAdd: IApplicationNumberUpdateNumbers = {
					number: num,
					action: "Update",
				};

				businessNumbers.push(businessNumberToAdd);
			});

			numbersToUnlink.map((number) => {
				const num: IApplicationNumberRemove = {
					businessNumber: number.businessNumber,
				};
				const businessNumberToAdd: IApplicationNumberUpdateNumbers = {
					number: num,
					action: "Remove",
				};

				businessNumbers.push(businessNumberToAdd);
			});

			try {
				await viewModel.current.submitApplicationAsync(
					applicationId,
					applicationName,
					description,
					businessNumbers,
					managedApplicationSettings
				);
				await delay(2000);
			} catch (error) {
				setIsProcessing(false);

				// Handle error as a string array
				if (typeof error === "string") {
					setErrors([error]); // Wrap the error string in an array
				} else if (error instanceof Error) {
					setErrors([error.message]); // Use the message property if it's an Error
				} else {
					setErrors(["An unknown error occurred"]); // Fallback for unknown error types
				}

				console.error("Error processing number:", error);
			}

			setIsProcessing(false);
		};

		processNumbers();
	}, [user, numbersToProcess, applicationId, businessId, brandingOption]);

	return (
		<div className="font-Poppins flex flex-col items-center justify-center w-full h-full gap-4">
			<Lottie
				autoplay
				loop={isProcessing}
				animationData={loadingAnimationData}
				style={{
					height: "100px",
					backgroundColor: "red",
				}}
			/>
			{isProcessing && (
				<span className="font-semibold text-lg text-black">
					Processing ....
				</span>
			)}
			{!isProcessing && errors.length === 0 && (
				<>
					<span className="font-semibold text-lg text-green-500">
						All numbers processed successfully!
					</span>
					{/* <div className="flex flex-col items-center justify-center gap-4 p-2 w-full">
						<div className="flex items-center  justify-between gap-4 w-full">
							<span className="font-Poppins font-medium text-sm text-left w-full">
								Number of newly added numbers
							</span>
							<span className="font-Poppins font-medium text-sm">
								{noOfAddedNumber}
							</span>
						</div>
						<div className="flex items-center justify-stretch gap-4 w-full">
							<span className="font-Poppins font-medium text-sm text-left w-full">
								Number of updated numbers
							</span>
							<span className="font-Poppins font-medium text-sm">
								{noOfUpdatedNumber}
							</span>
						</div>
						<div className="flex items-center justify-stretch gap-4 w-full">
							<span className="font-Poppins font-medium text-sm text-left w-full">
								Number of unlinked numbers
							</span>
							<span className="font-Poppins font-medium text-sm text-right">
								{noOfUnLinkedNumber}
							</span>
						</div>
					</div> */}
				</>
			)}
			{!isProcessing && errors.length > 0 && (
				<div className="text-red-500">
					<p className="font-Poppins font-semibold text-red-505">
						Some numbers failed to process
					</p>
					<ul>
						{errors.map((error, index) => (
							<li
								className="font-Poppins font-semibold text-red-505"
								key={index}
							>
								{error}
							</li>
						))}
					</ul>
				</div>
			)}

			<ButtonPro
				label={"Close"}
				onClick={onClose}
				disabled={isProcessing}
			></ButtonPro>
			{/* <button
				className={`p-4 ${isProcessing ? "bg-blue-100" : "bg-blue-500"} rounded-full px-24 font-semibold text-white mt-4`}
				onClick={onClose}
				disabled={isProcessing} // Disable button while processing
			>
				Close
			</button> */}
		</div>
	);
};

export default ApplicationSubmitProcessor;
